import React, { useCallback, useMemo, useState } from "react";
import { css } from "glamor";
import { FaPlus, FaStar } from "react-icons/fa";
import Modal from "../library/Modal";
import CreditCardIcon from "./CreditCardIcon";
import { cardBrandName, cardExpiryText } from "./financeSubscriptionHelpers";

// ─── Styles ──────────────────────────────────────────────────────────────────

const sectionTitleClass = css({
  fontFamily: "Montserrat, sans-serif",
  color: "#0F172A",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "20px",
  margin: "10px 0 4px",
});

const paymentMethodsListClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const paymentMethodCardClass = css({
  display: "grid",
  gridTemplateColumns: "1fr auto",
  alignItems: "center",
  gap: 12,
  width: "100%",
  minHeight: 61.194,
  padding: "13px 18px",
  background: "#FFFFFF",
  border: "0.612036px solid #E2E8F0",
  borderRadius: 8,
  "@media(max-width: 520px)": {
    gridTemplateColumns: "1fr",
    gap: 8,
    padding: "12px 14px",
  },
});

const paymentMethodInfoClass = css({
  display: "flex",
  alignItems: "center",
  gap: 10,
  minWidth: 0,
});

const paymentMethodNameClass = css({
  color: "#0F172A",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "18px",
  overflowWrap: "anywhere",
});

const paymentMethodExpiryClass = css({
  color: "#94A3B8",
  fontSize: 10,
  fontWeight: 400,
  lineHeight: "15px",
});

const paymentMethodActionsClass = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 10,
  minWidth: 0,
  "@media(max-width: 520px)": {
    justifyContent: "flex-start",
    paddingLeft: 26,
  },
});

const primaryPaymentBadgeClass = css({
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  height: 20,
  padding: "0 7px",
  border: "none",
  borderRadius: 4,
  background: "#F0F9FF",
  color: "#0369A1",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
  whiteSpace: "nowrap",
});

const paymentMethodActionClass = css({
  border: "none",
  padding: 0,
  background: "transparent",
  color: "#64748B",
  fontSize: 10,
  fontWeight: 400,
  fontFamily: "Montserrat",
  lineHeight: "16px",
  cursor: "pointer",
  whiteSpace: "nowrap",
  ":hover": {
    color: "#6488BC",
  },
  ":disabled": {
    color: "#CBD5E1",
    cursor: "not-allowed",
  },
});

const deletePaymentMethodActionClass = css({
  border: "none",
  padding: 0,
  background: "transparent",
  color: "#94A3B8",
  fontSize: 10,
  fontWeight: 400,
  lineHeight: "16px",
  cursor: "pointer",
  whiteSpace: "nowrap",
  ":hover": {
    color: "#DC2626",
  },
  ":disabled": {
    color: "#E2E8F0",
    cursor: "not-allowed",
  },
});

const disabledDeleteWrapperClass = css({
  position: "relative",
  display: "inline-flex",
  "::after": {
    content: '"Cancele as assinaturas ativas antes de excluir este cartão."',
    position: "absolute",
    bottom: "calc(100% + 6px)",
    right: 0,
    width: 200,
    padding: "6px 8px",
    background: "#1E293B",
    color: "#FFFFFF",
    fontSize: 11,
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 400,
    lineHeight: "16px",
    borderRadius: 6,
    whiteSpace: "normal",
    pointerEvents: "none",
    opacity: 0,
    transition: "opacity 0.15s",
    zIndex: 10,
  },
  ":hover::after": {
    opacity: 1,
  },
});

const addPaymentMethodButtonClass = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  height: 40,
  width: "100%",
  border: "1px dashed #CBD5E1",
  borderRadius: 8,
  background: "#FFFFFF",
  color: "#64748B",
  fontFamily: "Montserrat, sans-serif",
  fontSize: 11,
  fontWeight: 500,
  lineHeight: "16px",
  cursor: "pointer",
  ":hover": {
    borderColor: "#94A3B8",
    background: "#F8FAFC",
    color: "#475569",
  },
});

const addPaymentMethodButtonDisabledClass = css(addPaymentMethodButtonClass, {
  color: "#94A3B8",
  cursor: "not-allowed",
  ":hover": {
    borderColor: "#CBD5E1",
    background: "#FFFFFF",
    color: "#94A3B8",
  },
});

const noPaymentMethodsClass = css({
  padding: 14,
  background: "#FFFFFF",
  border: "0.6px solid #E2E8F0",
  borderRadius: 8,
  color: "#64748B",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
});

const deleteModalClass = css({
  fontFamily: "Montserrat, sans-serif",
  "&.ui.tiny.modal": {
    maxWidth: 420,
    borderRadius: 8,
  },
  ".header": {
    borderBottom: "0.6px solid #E2E8F0 !important",
    padding: "16px 18px !important",
    fontFamily: "Montserrat, sans-serif !important",
  },
  ".content": {
    padding: "18px !important",
    fontFamily: "Montserrat, sans-serif !important",
  },
});

const deleteModalTitleClass = css({
  fontFamily: "Montserrat, sans-serif",
  margin: 0,
  color: "#0F172A",
  fontSize: 18,
  fontWeight: 600,
  lineHeight: "27px",
});

const deleteModalDescriptionClass = css({
  fontFamily: "Montserrat, sans-serif",
  margin: "0 0 14px",
  color: "#64748B",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "21px",
});

const deleteModalCardClass = css({
  display: "flex",
  alignItems: "center",
  gap: 10,
  minWidth: 0,
  padding: "12px 14px",
  border: "0.6px solid #E2E8F0",
  borderRadius: 8,
  background: "#F8FAFC",
});

const deleteModalCardNameClass = css({
  fontFamily: "Montserrat, sans-serif",
  color: "#0F172A",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "20px",
  overflowWrap: "anywhere",
});

const deleteModalCardMetaClass = css({
  fontFamily: "Montserrat, sans-serif",
  marginTop: 2,
  color: "#64748B",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
});

const deleteModalActionsClass = css({
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  borderTop: "0.6px solid #E2E8F0",
  background: "#FFFFFF",
  padding: "12px 18px",
  "@media(max-width: 420px)": {
    flexDirection: "column-reverse",
  },
});

const deleteModalCancelButtonClass = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 42,
  padding: "0 16px",
  borderRadius: 6,
  border: "0.612036px solid #E2E8F0",
  background: "#FFFFFF",
  color: "#475569",
  fontFamily: "Montserrat, sans-serif",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "21px",
  cursor: "pointer",
  ":hover": {
    background: "#F8FAFC",
    color: "#0F172A",
  },
  ":disabled": {
    color: "#94A3B8",
    cursor: "not-allowed",
  },
  "@media(max-width: 420px)": {
    width: "100%",
  },
});

const deleteModalConfirmButtonClass = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 42,
  padding: "0 16px",
  borderRadius: 6,
  border: "0.612036px solid #B91C1C",
  background: "#B91C1C",
  color: "#FFFFFF",
  fontFamily: "Montserrat, sans-serif",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "21px",
  cursor: "pointer",
  ":hover": {
    background: "#991B1B",
    borderColor: "#991B1B",
  },
  ":disabled": {
    borderColor: "#FCA5A5",
    background: "#FCA5A5",
    cursor: "not-allowed",
  },
  "@media(max-width: 420px)": {
    width: "100%",
  },
});

// ─── Sub-componente interno ───────────────────────────────────────────────────

function DeletePaymentMethodModal({ card, loading, onClose, onConfirm }) {
  if (!card) return null;

  return (
    <Modal
      open={!!card}
      onClose={loading ? undefined : onClose}
      className={deleteModalClass}
      size="tiny"
    >
      <Modal.Header>
        <h2 className={deleteModalTitleClass}>Excluir método de pagamento</h2>
      </Modal.Header>
      <Modal.Content>
        <p className={deleteModalDescriptionClass}>
          Este cartão deixará de aparecer como método salvo.
        </p>
        <div className={deleteModalCardClass}>
          <CreditCardIcon />
          <div>
            <div className={deleteModalCardNameClass}>
              {cardBrandName(card.brand)} ****{card.number}
            </div>
            <div className={deleteModalCardMetaClass}>
              {cardExpiryText(card)}
            </div>
          </div>
        </div>
      </Modal.Content>
      <div className={deleteModalActionsClass}>
        <button
          type="button"
          className={deleteModalCancelButtonClass}
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </button>
        <button
          type="button"
          className={deleteModalConfirmButtonClass}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? "Excluindo..." : "Excluir método"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const TERMINAL_STATUSES = ["cancelled", "expired"];

function cardHasActiveSubscription(card, subscriptions) {
  return subscriptions.some(
    (s) =>
      s.cardId === card.cardId &&
      !s.resumedToId &&
      !TERMINAL_STATUSES.includes(s.status),
  );
}

export default function PaymentMethodsSection({
  creditCardTokens = [],
  subscriptions = [],
  onAddPaymentMethod,
  onDeletePaymentMethod,
  onSetDefaultPaymentMethod,
  canAddPaymentMethod,
  actionId,
}) {
  const [cardToDelete, setCardToDelete] = useState(null);
  const sortedCards = useMemo(
    () =>
      [...creditCardTokens].sort(
        (a, b) => Number(b.default) - Number(a.default),
      ),
    [creditCardTokens],
  );

  const handleSetDefault = useCallback(
    (card) => {
      onSetDefaultPaymentMethod(card.id);
    },
    [onSetDefaultPaymentMethod],
  );

  const handleDelete = useCallback((card) => {
    setCardToDelete(card);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setCardToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!cardToDelete) return;

    onDeletePaymentMethod(cardToDelete.id)
      .then(() => {
        setCardToDelete(null);
      })
      .catch(() => {});
  }, [cardToDelete, onDeletePaymentMethod]);

  return (
    <>
      <p className={sectionTitleClass}>Métodos de pagamento</p>
      <div className={paymentMethodsListClass}>
        {sortedCards.length ? (
          sortedCards.map((card) => (
            <div className={paymentMethodCardClass} key={card.id}>
              <div className={paymentMethodInfoClass}>
                <CreditCardIcon />
                <div>
                  <div className={paymentMethodNameClass}>
                    {cardBrandName(card.brand)} ****{card.number}
                  </div>
                  <div className={paymentMethodExpiryClass}>
                    {cardExpiryText(card)}
                  </div>
                </div>
              </div>

              <div className={paymentMethodActionsClass}>
                {card.default ? (
                  <span className={primaryPaymentBadgeClass}>
                    <FaStar size={10} />
                    Principal
                  </span>
                ) : (
                  <button
                    type="button"
                    className={paymentMethodActionClass}
                    onClick={() => handleSetDefault(card)}
                    disabled={!!actionId}
                  >
                    {actionId === `default-${card.id}`
                      ? "Atualizando..."
                      : "Definir como principal"}
                  </button>
                )}
                {cardHasActiveSubscription(card, subscriptions) ? (
                  <span className={disabledDeleteWrapperClass}>
                    <button
                      type="button"
                      className={deletePaymentMethodActionClass}
                      disabled
                      style={{ pointerEvents: "none" }}
                    >
                      Excluir
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={deletePaymentMethodActionClass}
                    onClick={() => handleDelete(card)}
                    disabled={!!actionId}
                  >
                    {actionId === `delete-${card.id}`
                      ? "Excluindo..."
                      : "Excluir"}
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className={noPaymentMethodsClass}>
            Nenhum método de pagamento cadastrado.
          </div>
        )}

        <button
          type="button"
          className={
            canAddPaymentMethod
              ? addPaymentMethodButtonClass
              : addPaymentMethodButtonDisabledClass
          }
          onClick={onAddPaymentMethod}
          disabled={!canAddPaymentMethod}
        >
          <FaPlus size={11} />
          Adicionar método de pagamento
        </button>
      </div>
      <DeletePaymentMethodModal
        card={cardToDelete}
        loading={
          cardToDelete ? actionId === `delete-${cardToDelete.id}` : false
        }
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
